import { useTranslation } from '../../i18n';
import { Skeleton, SkeletonAvatar, SkeletonRegion } from '@parsina/framework';
import { C } from '../../constants/theme';
import { doctorColor, getInitials } from '../Booking/helpers';
import AdaptiveModal from '../../components/common/AdaptiveModal';

/**
 * «ثبت نوبت»'s first step — who is this appointment WITH.
 *
 * One picker for both ways into booking: the day panel's «ثبت نوبت» lists the
 * clinic's doctors, and «اقدام‌های درمانی» › «نوبت بده» lists the people a service
 * declares as its performers — the doctor, or his coworkers. It was two pickers
 * once, and the second one looked like nothing else in the app.
 *
 * `people` — [{ key, id, name, meta, tag }]. `meta` is the muted line under the
 * name («ویزیت ۳۰ دقیقه», «همکار»); `tag` is an optional chip beside it
 * («پیش‌فرض»). The row's colour is derived from `id`, as everywhere a
 * practitioner is drawn.
 *
 * `notice` is a refusal the last pick met, shown above the list — a step of a
 * treatment process that is not ready yet (ORDER_NOT_READY) is the case.
 */
export default function PractitionerPickerModal({
  title, subtitle, people, loading = false, loadingLabel, onPick, onClose, empty = null, notice = null,
}) {
  const { t } = useTranslation();

  const picker = (
    <>
      {notice ? (
        <div
          role="alert"
          data-testid="practitioner-picker-notice"
          style={{
            fontSize: 12, lineHeight: 1.7, fontWeight: 700,
            color: 'var(--chip-warning-ink)', background: 'var(--chip-warning-bg)',
            borderRadius: 8, padding: '8px 10px',
          }}
        >
          {notice}
        </div>
      ) : null}
      {/* The practitioner rows in placeholder, at a row's own size (38px
          disc, name, visit length) so the list lands without a jump. */}
      {loading && (
        <SkeletonRegion
          label={loadingLabel || t('common.state.loading')}
          data-testid="booking-doctors-skeleton"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${C.border}`, background: 'white' }}>
              <SkeletonAvatar size={38} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Skeleton width="50%" height={14} />
                <Skeleton width="35%" height={11} />
              </div>
            </div>
          ))}
        </SkeletonRegion>
      )}
      {!loading && people.length === 0 ? empty : null}
      {people.map(p => (
        <button
          key={p.key}
          onClick={() => onPick(p)}
          data-testid={`practitioner-pick-${p.id}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%',
            padding: '12px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'end',
            border: `1.5px solid ${C.border}`, background: 'white',
            fontFamily: 'inherit', transition: 'border-color 0.14s, background 0.14s'
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.background = 'color-mix(in srgb, var(--p-navy) 6%, transparent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'white'; }}
        >
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: doctorColor(p.id), color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800
          }}>
            {getInitials(p.name)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{p.name}</span>
              {p.tag ? (
                <span style={{ fontSize: 10.5, fontWeight: 700, color: C.navy, background: 'color-mix(in srgb, var(--p-navy) 10%, transparent)', borderRadius: 999, padding: '1px 8px' }}>
                  {p.tag}
                </span>
              ) : null}
            </div>
            {p.meta ? (
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 2 }}>{p.meta}</div>
            ) : null}
          </div>
          <svg viewBox="0 0 24 24" fill={C.textMuted} width="15" height="15"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        </button>
      ))}
    </>
  );

  return (
    <AdaptiveModal
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      snap={0.85}
      fullHeight={false}
      desktop={(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(15,28,46,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
          onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={{
              background: 'white', borderRadius: 20, width: '100%', maxWidth: 420,
              maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 32px 80px rgba(0,0,0,0.26)', fontFamily: 'Meshkat, sans-serif'
            }}
          >
            <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.textPrimary }}>{title}</div>
                {subtitle ? <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>{subtitle}</div> : null}
              </div>
              <button onClick={onClose} aria-label={t('common.actions.close')} style={{ background: C.bg, border: 'none', cursor: 'pointer', color: C.textMuted, width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 22px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {picker}
            </div>
          </div>
        </div>
      )}
    >
      {picker}
    </AdaptiveModal>
  );
}
